import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getReleaseDetails } from "../../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../../helpers/aws_api_helper";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";
import RemediationPreviewPanel, {
  REMEDIATION_PREVIEW_PANEL_WIDTH_PX,
} from "../../../components/Common/RemediationPreviewPanel";

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const OUTLINED_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover:not(.Mui-error) fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused:not(.Mui-error) fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: "#64748b",
  },
  "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
    color: "#111827",
  },
  "& .MuiFormHelperText-root": {
    mx: 0.25,
    mt: 0.75,
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const INFO_ALERT_SX = {
  fontSize: "0.8125rem",
  borderRadius: "8px",
  backgroundColor: "#eff6ff",
  color: "#1e40af",
  border: "1px solid #bfdbfe",
  "& .MuiAlert-icon": { color: "#2563eb" },
  "& .MuiAlert-message": { width: "100%" },
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#adb5bd",
    borderColor: "#adb5bd",
    color: "#fff",
  },
};

const PRIMARY_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: BRAND_ORANGE,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

/** Matches Pair Account “Click to Launch” (#1976d2). */
const LAUNCH_BLUE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: "#1976d2",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1565c0",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const DIALOG_PAPER_SX = {
  borderRadius: "12px",
  m: 2,
  maxHeight: "90dvh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  ...ACCOUNT_DETAIL_FONT_SX,
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Current calendar year plus the four years before it. */
const CUR_PERIOD_YEAR_COUNT = 5;

const getCurPeriodBounds = (referenceDate = new Date()) => {
  const currentYear = referenceDate.getFullYear();
  const currentMonth = referenceDate.getMonth() + 1;
  return {
    currentYear,
    currentMonth,
    minYear: currentYear - (CUR_PERIOD_YEAR_COUNT - 1),
  };
};

const clampCurPeriod = (year, month, bounds) => {
  let nextYear = Number(year);
  if (!Number.isInteger(nextYear) || nextYear < bounds.minYear || nextYear > bounds.currentYear) {
    nextYear = bounds.currentYear;
  }
  const maxMonth = nextYear === bounds.currentYear ? bounds.currentMonth : 12;
  let nextMonth = Number(month);
  if (!Number.isInteger(nextMonth) || nextMonth < 1) nextMonth = 1;
  if (nextMonth > maxMonth) nextMonth = maxMonth;
  return { year: nextYear, month: nextMonth };
};

/**
 * Same `fileName` as Configuration → Add-ons → FinOps (Cost Insight) manual configuration
 * (`getReleaseDetails` / `GET cloudnova/documentation?fileName=...`).
 */
const getFinOpsManualGuideFileName = () => {
  const fromEnv = process.env.REACT_APP_COST_INSIGHT_MANUAL_CONFIGURATION_GUIDE;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim();
  }
  return "configurations/Cost_Insight_Manual_Guide.pdf";
};

/** Manual panel max width (right) — matches Edit Account remediation / guide panel. */
const OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX = REMEDIATION_PREVIEW_PANEL_WIDTH_PX;
const OFFLINE_CUR_DIALOG_MR_WITH_MANUAL = `min(${24 + OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px, calc(40vw + 24px))`;
/** Above MUI Dialog (1300) so Confirm stays clickable over the manual panel. */
const OFFLINE_CUR_MANUAL_Z_INDEX = 1400;
const OFFLINE_CUR_CONFIRM_Z_INDEX = 1500;

const PREVIEW_PANEL_COLORS = {
  card: "#ffffff",
  border: "#e4e4e7",
  borderLight: "#f4f4f5",
  text: "#18181b",
  textMuted: "#71717a",
  background: "#fafafa",
};

/** `GET cloudnova/documentation` may return the signed URL in `url`, `signedUrl`, or other keys. */
const isHttpUrlString = (v) => typeof v === "string" && /^https?:\/\//i.test(v.trim());

const pickDocumentationUrlFromReleaseResponse = (response) => {
  if (response == null) return "";
  const status = Number(response.status);
  if (Number.isNaN(status) || status !== 200) return "";
  const named = [
    response.url,
    response.signedUrl,
    response.documentUrl,
    response.fileUrl,
    response.link,
    response.href,
  ];
  for (const v of named) {
    if (isHttpUrlString(v)) return v.trim();
  }
  const skip = new Set(["status", "message", "error", "data", "isSynthetic"]);
  if (response && typeof response === "object") {
    for (const [k, v] of Object.entries(response)) {
      if (skip.has(k) || v == null) continue;
      if (isHttpUrlString(v)) return String(v).trim();
    }
  }
  return "";
};

/** AWS account IDs are exactly 12 digits. */
const AWS_ACCOUNT_NUMBER_PATTERN = /^\d{12}$/;

const toAccountDigits = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 12);

const ACCOUNT_AUTOCOMPLETE_SLOT_PROPS = {
  popper: {
    sx: { zIndex: 1600 },
  },
  paper: {
    sx: {
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiAutocomplete-listbox": {
        maxHeight: 280,
        py: 0.5,
      },
      "& .MuiAutocomplete-option": {
        fontSize: "0.875rem",
        minHeight: 36,
      },
      "& .MuiAutocomplete-option.Mui-focused": {
        backgroundColor: "#fff4eb",
      },
      "& .MuiAutocomplete-option[aria-selected='true']": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading": {
        fontSize: "0.8125rem",
        color: "#64748b",
      },
    },
  },
};

const UploadOfflineCurModal = ({
  open,
  onClose,
  onSubmit,
  isUploading,
  uploadProgress,
  onRequestCancelUpload,
  accountNumbers = [],
  accountsLoading = false,
}) => {
  const now = new Date();
  const [accountNumber, setAccountNumber] = useState("");
  const [accountTouched, setAccountTouched] = useState(false);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formError, setFormError] = useState("");
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [manualPanelOpen, setManualPanelOpen] = useState(true);

  const [finOpsManualGuideUrl, setFinOpsManualGuideUrl] = useState("");
  const [finOpsManualGuideLoading, setFinOpsManualGuideLoading] = useState(false);
  const [finOpsManualGuideError, setFinOpsManualGuideError] = useState("");
  /** Alias: API-fetched document URL (some bundles/hot-reloads may still reference the old name). */
  const finOpsManualGuideSrc = finOpsManualGuideUrl;

  useEffect(() => {
    if (!open) {
      setFinOpsManualGuideUrl("");
      setFinOpsManualGuideLoading(false);
      setFinOpsManualGuideError("");
      setUploadConfirmOpen(false);
      setManualPanelOpen(true);
      return;
    }
    setManualPanelOpen(true);

    let cancelled = false;
    const fileName = getFinOpsManualGuideFileName();

    (async () => {
      setFinOpsManualGuideLoading(true);
      setFinOpsManualGuideError("");
      setFinOpsManualGuideUrl("");
      try {
        const response = await getReleaseDetails(fileName);
        if (cancelled) return;
        const docUrl = pickDocumentationUrlFromReleaseResponse(response);
        if (docUrl) {
          setFinOpsManualGuideUrl(docUrl);
        } else {
          setFinOpsManualGuideError(
            getApiErrorMessage(response) || "Could not load the FinOps manual document."
          );
        }
      } catch (e) {
        if (!cancelled) {
          setFinOpsManualGuideError(
            getApiErrorMessage(e?.data ?? e) || e?.message || "Could not load the FinOps manual document."
          );
        }
      } finally {
        if (!cancelled) setFinOpsManualGuideLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const periodBounds = useMemo(
    () => getCurPeriodBounds(new Date(currentYear, currentMonth - 1, 1)),
    [currentYear, currentMonth]
  );
  const selectedPeriod = clampCurPeriod(year, month, periodBounds);
  const yearOptions = useMemo(() => {
    const years = [];
    for (let optionYear = periodBounds.currentYear; optionYear >= periodBounds.minYear; optionYear -= 1) {
      years.push(optionYear);
    }
    return years;
  }, [periodBounds]);
  const monthOptions = useMemo(() => {
    const maxMonth =
      selectedPeriod.year === periodBounds.currentYear ? periodBounds.currentMonth : 12;
    return monthNames.slice(0, maxMonth).map((monthName, index) => ({
      value: index + 1,
      label: monthName,
    }));
  }, [periodBounds, selectedPeriod.year]);

  useEffect(() => {
    if (selectedPeriod.year !== year) setYear(selectedPeriod.year);
    if (selectedPeriod.month !== month) setMonth(selectedPeriod.month);
  }, [month, selectedPeriod.month, selectedPeriod.year, year]);

  const accountNumberOptions = useMemo(() => {
    if (!Array.isArray(accountNumbers)) return [];
    const seen = new Set();
    const list = [];
    accountNumbers.forEach((item) => {
      const value = item != null ? String(item).trim() : "";
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push(value);
    });
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [accountNumbers]);

  const normalizedAccountNumber = accountNumber.trim();
  const isValidAccountNumber = AWS_ACCOUNT_NUMBER_PATTERN.test(normalizedAccountNumber);
  const accountFieldError = !accountTouched
    ? ""
    : !normalizedAccountNumber
      ? "Account number is required."
      : !isValidAccountNumber
        ? "Enter a 12-digit AWS account number."
        : "";

  const organizationDisplayName = useMemo(() => {
    try {
      const raw = localStorage.getItem("selectedOrganization");
      if (!raw) return "";
      const o = JSON.parse(raw);
      const n = o?.orgName ?? o?.organizationName ?? o?.org_name;
      return n != null ? String(n).trim() : "";
    } catch {
      return "";
    }
  }, [open]);

  const isPeriodAllowed =
    selectedPeriod.year >= periodBounds.minYear &&
    selectedPeriod.year <= periodBounds.currentYear &&
    selectedPeriod.month >= 1 &&
    selectedPeriod.month <=
      (selectedPeriod.year === periodBounds.currentYear ? periodBounds.currentMonth : 12);

  const isFormComplete =
    isValidAccountNumber && isPeriodAllowed && selectedFiles.length > 0;

  const handleFileChange = (event) => {
    const list = Array.from(event.target.files || []);
    if (list.length === 0) {
      setSelectedFiles([]);
      if (event.target) event.target.value = "";
      return;
    }
    const valid = list.filter((f) => f.name.toLowerCase().endsWith(".gz"));
    if (valid.length === 0) {
      setFormError("Only gzip files (.gz) are supported.");
      setSelectedFiles([]);
    } else {
      if (valid.length < list.length) {
        setFormError(`${list.length - valid.length} file(s) skipped (not .gz).`);
      } else {
        setFormError("");
      }
      setSelectedFiles(valid);
    }
    if (event.target) event.target.value = "";
  };

  const resetModalState = () => {
    setAccountNumber("");
    setAccountTouched(false);
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedFiles([]);
    setFormError("");
  };

  const finishClose = () => {
    resetModalState();
    onClose();
  };

  const handleClose = () => {
    if (isUploading) {
      onRequestCancelUpload?.();
      return;
    }
    finishClose();
  };

  const validateBeforeUpload = () => {
    if (!isValidAccountNumber) {
      setAccountTouched(true);
      return false;
    }
    if (!isPeriodAllowed) {
      setFormError("Select a month and year from the last 5 years, up to the current month.");
      return false;
    }
    if (selectedFiles.length === 0) {
      setFormError("Please select at least one file to upload.");
      return false;
    }
    if (selectedFiles.some((f) => !f.name.toLowerCase().endsWith(".gz"))) {
      setFormError("Only gzip files (.gz) are supported.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleUploadClick = () => {
    if (!validateBeforeUpload()) return;
    setUploadConfirmOpen(true);
  };

  const handleConfirmUpload = async () => {
    setUploadConfirmOpen(false);
    setFormError("");
    const success = await onSubmit({
      accountNumber: normalizedAccountNumber,
      year: selectedPeriod.year,
      month: selectedPeriod.month,
      files: selectedFiles,
    });

    if (success) {
      finishClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Backdrop click must not close the upload stepper (users often click away from the manual).
        if (reason === "backdropClick") return;
        if (isUploading) {
          onRequestCancelUpload?.();
          return;
        }
        finishClose();
      }}
      fullWidth
      maxWidth="sm"
      /* Centered dialog; scrollable under zoom so actions stay reachable. */
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "center",
          alignItems: "center",
          overflowX: "hidden",
          overflowY: "auto",
        },
      }}
      PaperProps={{
        sx: {
          ...DIALOG_PAPER_SX,
          ...(manualPanelOpen
            ? { mr: { md: OFFLINE_CUR_DIALOG_MR_WITH_MANUAL } }
            : {}),
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#FFFFFF",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Upload CUR2.0
        </Typography>
        <button
          type="button"
          aria-label="Close upload dialog"
          onClick={handleClose}
          disabled={isUploading}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isUploading ? "not-allowed" : "pointer",
            opacity: isUploading ? 0.6 : 1,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Box>
      <DialogContent sx={{ overflowY: "auto", minHeight: 0, px: 2.5, py: 2.5, backgroundColor: "#fff" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {formError ? (
            <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
              {formError}
            </Alert>
          ) : null}
          <Alert severity="info" sx={INFO_ALERT_SX}>
            <Box component="span" sx={{ display: "block" }}>
              • Enter CUR 2.0 Source Account Number
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Accepts .gz files from CUR 2.0 exports only.
            </Box>
            <Box component="span" sx={{ display: "block", mt: 1.25 }}>
              • Multi-part CUR 2.0 file upload allowed for single month
            </Box>
          </Alert>
          <Autocomplete
            id="offline-cur-upload-account"
            freeSolo
            forcePopupIcon
            openOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            disabled={isUploading}
            fullWidth
            size="small"
            options={accountNumberOptions}
            loading={accountsLoading}
            value={accountNumber}
            inputValue={accountNumber}
            onChange={(_, value) => {
              setAccountNumber(toAccountDigits(value));
            }}
            onInputChange={(_, value, reason) => {
              if (reason === "reset") return;
              setAccountNumber(toAccountDigits(value));
              if (reason === "blur" || reason === "selectOption") {
                setAccountTouched(true);
              }
            }}
            filterOptions={(options, state) => {
              const query = toAccountDigits(state.inputValue);
              if (!query) return options;
              return options.filter((option) => String(option).includes(query));
            }}
            isOptionEqualToValue={(option, value) => option === value}
            slotProps={ACCOUNT_AUTOCOMPLETE_SLOT_PROPS}
            noOptionsText={
              accountsLoading
                ? "Loading accounts…"
                : accountNumberOptions.length === 0
                  ? "No saved accounts. Enter a 12-digit AWS account number."
                  : "No matching accounts."
            }
            loadingText="Loading accounts…"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account number"
                placeholder={
                  accountsLoading ? "Loading accounts…" : "Select or enter a 12-digit account"
                }
                error={Boolean(accountFieldError)}
                sx={OUTLINED_FIELD_SX}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                }}
                inputProps={{
                  ...params.inputProps,
                  inputMode: "numeric",
                  autoComplete: "off",
                  "aria-label": "Account number",
                  "aria-describedby": accountFieldError
                    ? "offline-cur-upload-account-error"
                    : params.inputProps["aria-describedby"],
                  onBlur: (event) => {
                    params.inputProps.onBlur?.(event);
                    setAccountTouched(true);
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {accountsLoading ? (
                        <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <FormControl size="small" fullWidth sx={OUTLINED_FIELD_SX} disabled={isUploading}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedPeriod.month}
                label="Month"
                onChange={(e) => {
                  const next = clampCurPeriod(
                    selectedPeriod.year,
                    Number(e.target.value),
                    periodBounds
                  );
                  setMonth(next.month);
                }}
                MenuProps={SELECT_MENU_PROPS}
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={OUTLINED_FIELD_SX} disabled={isUploading}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedPeriod.year}
                label="Year"
                onChange={(e) => {
                  const next = clampCurPeriod(Number(e.target.value), selectedPeriod.month, periodBounds);
                  setYear(next.year);
                  setMonth(next.month);
                }}
                MenuProps={SELECT_MENU_PROPS}
              >
                {yearOptions.map((optionYear) => (
                  <MenuItem key={optionYear} value={optionYear}>
                    {optionYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <input
              id="offline-cur-file-input"
              type="file"
              multiple
              accept=".gz,application/gzip"
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={handleFileChange}
            />
            <label htmlFor="offline-cur-file-input" style={isUploading ? { pointerEvents: "none" } : undefined}>
              <Button
                component="span"
                variant="contained"
                disableElevation
                disabled={isUploading}
                sx={{
                  ...LAUNCH_BLUE_BTN_SX,
                  height: 40,
                }}
              >
                {selectedFiles.length > 0 ? "Change file(s)" : "Choose file(s)"}
              </Button>
            </label>
            {selectedFiles.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  mt: 1,
                  pl: 2.25,
                  m: 0,
                  maxHeight: 160,
                  overflowY: "auto",
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  listStyle: "disc",
                }}
              >
                {selectedFiles.map((f) => (
                  <Box
                    key={`${f.name}-${f.size}-${f.lastModified}`}
                    component="li"
                    sx={{ wordBreak: "break-all", display: "list-item" }}
                  >
                    {f.name}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: "#64748b" }}>No file(s) selected</Typography>
            )}
          </Box>
          {isUploading && !uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  borderRadius: "8px",
                  height: 8,
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
                }}
              />
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                {uploadProgress?.fileCount > 1
                  ? `Preparing file ${uploadProgress.fileIndex} of ${uploadProgress.fileCount}…`
                  : "Preparing…"}
              </Typography>
              {uploadProgress?.fileCount > 1 && uploadProgress?.currentFileName ? (
                <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", wordBreak: "break-all" }}>
                  {uploadProgress.currentFileName}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {isUploading && uploadProgress?.total ? (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (uploadProgress.completed / uploadProgress.total) * 100)}
                sx={{
                  borderRadius: "8px",
                  height: 8,
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
                }}
              />
              {uploadProgress?.fileCount > 1 && (
                <Typography sx={{ mt: 0.5, fontSize: "0.7rem", color: "#64748b", wordBreak: "break-all" }}>
                  File {uploadProgress.fileIndex} of {uploadProgress.fileCount}
                  {uploadProgress.currentFileName ? `: ${uploadProgress.currentFileName}` : ""}
                </Typography>
              )}
              <Typography sx={{ mt: 0.75, fontSize: "0.75rem", color: "#64748b" }}>
                Uploading part {uploadProgress.completed} of {uploadProgress.total}…
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          flexShrink: 0,
          gap: 1,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          disableElevation
          sx={GREY_CLOSE_BTN_SX}
        >
          {isUploading ? "Cancel upload" : "Cancel"}
        </Button>
        <Box
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
          aria-live="polite"
        >
          {accountFieldError ? (
            <Typography
              id="offline-cur-upload-account-error"
              role="alert"
              sx={{
                width: "100%",
                fontSize: "0.8125rem",
                lineHeight: 1.35,
                fontWeight: 500,
                color: "#d32f2f",
                textAlign: "center",
                overflowWrap: "anywhere",
              }}
            >
              {accountFieldError}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={handleUploadClick}
          disabled={isUploading || !isFormComplete}
          sx={PRIMARY_BTN_SX}
        >
          {isUploading ? (uploadProgress?.total ? "Uploading…" : "Preparing…") : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={uploadConfirmOpen}
      onClose={() => setUploadConfirmOpen(false)}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
      sx={{ zIndex: OFFLINE_CUR_CONFIRM_Z_INDEX }}
      PaperProps={{
        sx: DIALOG_PAPER_SX,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          py: 1.75,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.0625rem",
            color: "#FFFFFF",
            lineHeight: 1.3,
          }}
        >
          Confirm upload
        </Typography>
      </Box>
      <DialogContent sx={{ overflowY: "auto", minHeight: 0, px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.6 }} component="div">
          {selectedFiles.length === 1 ? (
            <>
              This file <strong>{selectedFiles[0].name}</strong> will be uploaded into the system for CUR
              processing for account number : <strong>{normalizedAccountNumber}</strong> and your organization
              : <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : selectedFiles.length > 1 ? (
            <>
              These files:{" "}
              {selectedFiles.map((f, i) => (
                <React.Fragment key={`${f.name}-${f.size}-${f.lastModified}`}>
                  {i > 0 ? ", " : null}
                  <strong>{f.name}</strong>
                </React.Fragment>
              ))}{" "}
              will be uploaded into the system for CUR processing for account number :{" "}
              <strong>{normalizedAccountNumber}</strong> and your organization :{" "}
              <strong>{organizationDisplayName || "—"}</strong>. Please confirm to proceed.
            </>
          ) : null}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          flexShrink: 0,
          gap: 1,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={() => setUploadConfirmOpen(false)}
          variant="contained"
          disableElevation
          sx={GREY_CLOSE_BTN_SX}
        >
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleConfirmUpload} sx={PRIMARY_BTN_SX}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    {open && manualPanelOpen && finOpsManualGuideSrc ? (
      <RemediationPreviewPanel
        url={finOpsManualGuideSrc}
        onClose={() => setManualPanelOpen(false)}
        title="CUR 2.0 manual"
      />
    ) : null}
    {open && manualPanelOpen && !finOpsManualGuideSrc
      ? createPortal(
          <Paper
            elevation={8}
            component="aside"
            aria-label="CUR 2.0 manual document preview"
            sx={{
              position: "fixed",
              right: 0,
              top: 0,
              bottom: 0,
              left: { xs: 0, md: "auto" },
              width: {
                xs: "100vw",
                md: `${OFFLINE_CUR_MANUAL_PANEL_WIDTH_PX}px`,
              },
              minWidth: 0,
              maxWidth: "100vw",
              maxHeight: "100dvh",
              zIndex: OFFLINE_CUR_MANUAL_Z_INDEX,
              borderRadius: 0,
              border: `1px solid ${PREVIEW_PANEL_COLORS.border}`,
              borderTop: 0,
              borderBottom: 0,
              borderRight: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              boxSizing: "border-box",
              backgroundColor: PREVIEW_PANEL_COLORS.card,
            }}
          >
            <Box
              component="header"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                minHeight: 44,
                px: 1.5,
                py: 0.75,
                backgroundColor: PREVIEW_PANEL_COLORS.borderLight,
                borderBottom: `1px solid ${PREVIEW_PANEL_COLORS.border}`,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: PREVIEW_PANEL_COLORS.text,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title="CUR 2.0 manual"
              >
                CUR 2.0 manual
              </Typography>
              <IconButton
                onClick={() => setManualPanelOpen(false)}
                size="small"
                aria-label="Close CUR 2.0 manual"
                sx={{
                  color: PREVIEW_PANEL_COLORS.textMuted,
                  "&:hover": { color: PREVIEW_PANEL_COLORS.text },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {finOpsManualGuideError ? (
              <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
                <Alert
                  severity="error"
                  sx={{
                    fontSize: "0.75rem",
                    py: 0,
                    "& .MuiAlert-message": { padding: 0.75 },
                  }}
                >
                  {finOpsManualGuideError}
                </Alert>
              </Box>
            ) : null}
            {finOpsManualGuideLoading && !finOpsManualGuideError ? (
              <Box
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: PREVIEW_PANEL_COLORS.background,
                }}
              >
                <CircularProgress size={40} />
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: "0.8125rem",
                    color: PREVIEW_PANEL_COLORS.textMuted,
                  }}
                >
                  Loading manual preview…
                </Typography>
              </Box>
            ) : null}
            {!finOpsManualGuideError && !finOpsManualGuideLoading ? (
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: PREVIEW_PANEL_COLORS.background,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: PREVIEW_PANEL_COLORS.textMuted,
                  }}
                >
                  No document URL returned.
                </Typography>
              </Box>
            ) : null}
          </Paper>,
          document.body
        )
      : null}
    </>
  );
};

export default UploadOfflineCurModal;
